import { AbstractClient } from './AbstractClient';
import {
  OwlSubmissionInfo,
  SubmissionListResponse,
  AddSubmissionsToTicketPayload,
  CreateTicketPayload,
  CreateTicketResponse,
  SubmissionQuery,
  JiraPriorities,
  JiraStatuses,
} from '../../types/owl';
import {
  JiraSearchResponse,
  OwlResponse,
  AutoApprovalRate,
  CarrierDataExtracted,
  PagedOwlResponse,
  Policy,
  Submission,
} from '../../types';

export interface CarrierSubmissionData {
  carrier_name: string;
  total_submission_count: number;
  approved_submission_count: number;
  number_of_mfa_required_logins: number;
  percentage_of_total_approved: number;
  percentage_auto_approved: number;
  percentage_approved_with_data_extracted: number;
  auto_approval_enabled: boolean;
}

export class DeveloperClient extends AbstractClient {
  getPolicyInfo = async (submissionId: string) => {
    const func = this.createGetHandler<
      {
        data: OwlSubmissionInfo;
      },
      {
        args: {
          submissionId: string;
        };
      }
    >((args) => `/client-submission/${args.submissionId}`);
    const response = await func({
      args: { submissionId },
    });
    return response.data;
  };

  getSubmissions = this.createGetHandler<
    SubmissionListResponse,
    SubmissionQuery
  >('/client-submission');

  getCarrierSubmissionData = async (
    daysBack: number,
    page: number,
    perPage: number,
  ) => {
    const func = this.createGetHandler<
      {
        data: Array<CarrierSubmissionData>;
        meta: {
          last_page: number;
        };
      },
      {
        query: {
          daysBack: number;
          page: number;
          per_page: number;
        };
      }
    >('/carrier-submission');
    const data = await func({
      query: {
        daysBack,
        page,
        per_page: perPage,
      },
    });
    return data;
  };

  async searchForJiraTickets({
    statuses,
    searchTerm,
    priorities,
    page = 1,
    pageSize = 15,
  }: {
    statuses: JiraStatuses[];
    searchTerm?: string;
    priorities?: JiraPriorities[];
    page: number;
    pageSize: number;
  }) {
    const getHandler = this.createGetHandler<
      { data: JiraSearchResponse[] },
      {
        query: {
          statuses: JiraStatuses[];
          searchTerm?: string;
          page: number;
          pageSize: number;
          priorities?: string[];
        };
      }
    >('/jira/client-submission-issue-search');

    const data = await getHandler({
      query: { statuses, searchTerm, page, pageSize, priorities },
    });
    return data.data;
  }

  async createJiraTicket(payload: CreateTicketPayload) {
    const payloadHandler = this.createPayloadHandler<
      OwlResponse<CreateTicketResponse>,
      { payload: CreateTicketPayload }
    >('POST', '/jira/issue-for-client-submissions');

    const data = await payloadHandler({ payload });
    return data.data;
  }

  async addSubmissionsToTicket(payload: AddSubmissionsToTicketPayload) {
    const payloadHandler = this.createPayloadHandler<
      OwlResponse<any>,
      { payload: { clientSubmissionUuids: string[] } }
    >('POST', `/jira/issue/${payload.ticket}/add-client-submissions-to-issue`);

    const data = await payloadHandler({
      payload: { clientSubmissionUuids: payload.clientSubmissionUuids },
    });

    return data.data;
  }

  async getAutoApprovalRates() {
    const getHandler = this.createGetHandler<
      { data: AutoApprovalRate[] },
      Record<string, never>
    >('/carriers/not-meeting-auto-approval-sla');

    const data = await getHandler({});

    return data.data;
  }

  async getCarrierDataExtracted(carrierSlug: string) {
    const getHandler = this.createGetHandler<
      {
        data: CarrierDataExtracted[];
      },
      { query: { carrierSlug: string } }
    >('/carriers/data-extraction-by-carrier');
    const data = await getHandler({
      query: { carrierSlug },
    });

    return data.data.find((carrier) => carrier.carrierSlug === carrierSlug);
  }

  getPoliciesList = this.createGetHandler<
    PagedOwlResponse<(Policy & { clientSubmission: Submission })[]>,
    {
      query: {
        carriers?: string;
        page?: number;
        perPage?: number;
        policyTypes?: string;
      };
    }
  >('client-submission-policies');

  getPolicyDecPage = this.createGetHandler<
    OwlResponse<{ decPageUrl: string }>,
    {
      args: { policyUUID: string };
    }
  >(({ policyUUID }) => `policy/${policyUUID}/dec-page`);
}
